import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, IconButton, TextField } from '@material-ui/core';
import { Add, ArrowBack, ArrowForward, Close } from '@material-ui/icons';
import { toast } from 'react-toastify';

import { parseEmenta } from '../../../../../../services/certificateBatches';
import { useDebounce } from '../../../../../../hooks/useDebounce';
import {
  ActionsRow,
  PasteRow,
  ModuleList,
  ModuleCard,
  ModuleHeader,
  AddModuleRow,
  WarningText,
  EmentaColumns,
  EditorColumn,
  PreviewColumn,
  PreviewHeading,
  PreviewViewport,
  PreviewPage,
} from './styles';

const MAX_RECOMMENDED_ITEMS = 300;

const nextModule = (modules) => ({ titulo: `Módulo ${modules.length + 1}`, itens: [] });

// value: array de módulos { titulo, itens: string[] }; onNext(finalEmenta)
// persiste a ementa e avança pro passo de alunos.
export default function EmentaStep({ value, onChange, onBack, onNext, isSaving, includeEmenta }) {
  const ementa = value || [];
  const [pasteText, setPasteText] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [count, setCount] = useState(0);
  const [isParsingPaste, setIsParsingPaste] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  // Texto bruto por módulo (índice -> string digitada). Existe pra não perder
  // a linha vazia que o Enter acabou de criar antes do split/filter limpar
  // o array de itens que o pai guarda.
  const [rawItens, setRawItens] = useState({});

  const debouncedEmenta = useDebounce(ementa, 500);
  const refreshSeq = useRef(0);
  // Snapshot (JSON) da última ementa que já veio com preview pronto do
  // parse do paste. Comparação idempotente: só pula o fetch quando a
  // ementa debounced ainda é exatamente essa — não consome/reseta nada,
  // então uma edição rápida pós-paste (mesmo <500ms) sempre dispara um
  // novo preview.
  const lastParsedRef = useRef(null);

  useEffect(() => {
    const seq = ++refreshSeq.current;

    if (
      lastParsedRef.current !== null &&
      JSON.stringify(debouncedEmenta) === lastParsedRef.current
    ) {
      return;
    }

    if (!debouncedEmenta.length) {
      setPreviewHtml('');
      setCount(0);
      return;
    }

    setIsPreviewLoading(true);
    parseEmenta({ ementa: debouncedEmenta })
      .then(({ data }) => {
        if (seq !== refreshSeq.current) return;
        setPreviewHtml(data.html);
        setCount(data.count);
      })
      .catch(() => {
        if (seq !== refreshSeq.current) return;
        toast.error('Não foi possível atualizar o preview da ementa.');
      })
      .finally(() => {
        if (seq === refreshSeq.current) setIsPreviewLoading(false);
      });
  }, [debouncedEmenta]);

  const handlePaste = async () => {
    if (!pasteText.trim()) return;
    setIsParsingPaste(true);
    try {
      const { data } = await parseEmenta({ text: pasteText });
      lastParsedRef.current = JSON.stringify(data.ementa);
      onChange(data.ementa);
      setPreviewHtml(data.html);
      setCount(data.count);
      setPasteText('');
      setRawItens({});
    } catch (error) {
      toast.error('Não foi possível organizar o texto colado. Confira o formato e tente novamente.');
    } finally {
      setIsParsingPaste(false);
    }
  };

  const updateModule = (i, patch) => {
    onChange(ementa.map((mod, idx) => (idx === i ? { ...mod, ...patch } : mod)));
  };

  const updateModuleItensText = (i, text) => {
    setRawItens((prev) => ({ ...prev, [i]: text }));
    updateModule(i, {
      itens: text
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
    });
  };

  const removeModule = (i) => {
    onChange(ementa.filter((_, idx) => idx !== i));
    setRawItens({});
  };

  const addModule = () => {
    onChange([...ementa, nextModule(ementa)]);
    setRawItens({});
  };

  if (includeEmenta === false) {
    return (
      <div>
        <WarningText>
          Ementa desativada no passo anterior — o certificado sairá sem página 2.
        </WarningText>

        <ActionsRow>
          <Button type="button" startIcon={<ArrowBack />} onClick={onBack} disabled={isSaving}>
            Voltar
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
            disabled={isSaving}
            onClick={() => onNext(ementa || [])}
          >
            Avançar
          </Button>
        </ActionsRow>
      </div>
    );
  }

  return (
    <div>
      <EmentaColumns>
        <EditorColumn>
          <PasteRow>
            <TextField
              multiline
              rows={6}
              label="Colar conteúdo do PPT"
              placeholder={'Cole o conteúdo do PPT aqui…\nMódulo 1\nFundamentos\n- Item A\n- Item B'}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              fullWidth
            />
            <Button
              type="button"
              variant="outlined"
              disabled={!pasteText.trim() || isParsingPaste}
              onClick={handlePaste}
            >
              {isParsingPaste ? <CircularProgress size={20} /> : 'Organizar texto colado'}
            </Button>
          </PasteRow>

          <ModuleList>
            {ementa.map((mod, i) => (
              <ModuleCard key={i}>
                <ModuleHeader>
                  <TextField
                    label={`Módulo ${i + 1}`}
                    value={mod.titulo}
                    onChange={(e) => updateModule(i, { titulo: e.target.value })}
                    fullWidth
                  />
                  <IconButton
                    type="button"
                    size="small"
                    aria-label="Remover módulo"
                    onClick={() => removeModule(i)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </ModuleHeader>
                <TextField
                  multiline
                  rows={Math.min(10, mod.itens.length + 1)}
                  label="Itens (um por linha)"
                  value={rawItens[i] !== undefined ? rawItens[i] : mod.itens.join('\n')}
                  onChange={(e) => updateModuleItensText(i, e.target.value)}
                  fullWidth
                />
              </ModuleCard>
            ))}
          </ModuleList>

          <AddModuleRow>
            <Button type="button" startIcon={<Add />} onClick={addModule}>
              Módulo
            </Button>
          </AddModuleRow>

          {count > MAX_RECOMMENDED_ITEMS && (
            <WarningText>
              {count} itens — acima de ~{MAX_RECOMMENDED_ITEMS} o template não garante caber na
              página 2.
            </WarningText>
          )}
        </EditorColumn>

        <PreviewColumn>
          <PreviewHeading>
            Preview da página 2 {isPreviewLoading && <CircularProgress size={12} />}
          </PreviewHeading>
          <PreviewViewport>
            {/* eslint-disable-next-line react/no-danger */}
            {/* O HTML vem do nosso backend (EmentaBuilder escapa todo conteúdo
                do usuário), então dangerouslySetInnerHTML é seguro aqui. */}
            <PreviewPage dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </PreviewViewport>
        </PreviewColumn>
      </EmentaColumns>

      <ActionsRow>
        <Button type="button" startIcon={<ArrowBack />} onClick={onBack} disabled={isSaving}>
          Voltar
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
          disabled={!ementa.length || isSaving}
          onClick={() => onNext(ementa)}
        >
          Avançar
        </Button>
      </ActionsRow>
    </div>
  );
}
